import { TWidget } from 'widgets/Widget';
import { RefElement } from 'widgets/toolbox/RefElement';
import { submitFormJsonWithAbort } from 'widgets/toolbox/ajax';
import { TAddToCartButtonInstance } from './AddToCartButton';

const ariaChecked = 'aria-checked';

function ProductDetailCrossSellMixinClassCreator(Widget: TWidget) {
    class ProductDetailCrossSellMixin extends Widget {
        prefs() {
            return {
                addtoCartMsg: 'addtoCartMsg',
                textSelectSize: '',
                textNetworkError: '',
                ...super.prefs()
            };
        }

        init() {
            const rootEl = this.refs.self.get(0);
            const selectDivs = rootEl?.querySelectorAll('.b-select');

            if (selectDivs?.length === 1) {
                this.toggleHidden(selectDivs[0] as HTMLElement);
            }

            this.eventBus().on('product.tile.swatch.select', 'handleSwatchSelection');

            const selectedSwatch = rootEl?.querySelector<HTMLElement>('[data-ref="swatchButton"].m-selected');

            this.handleSwatchSelection(new RefElement(selectedSwatch ? [selectedSwatch] : []));
        }

        /**
         * Triggers add-to-cart flow if the method exists on the instance.
         *
         * button {TAddToCartButtonInstance} - The add to cart button.
         */
        handleAddToCart(button: TAddToCartButtonInstance) {
            if (!('addToCart' in this && typeof this.addToCart === 'function')) {
                return;
            }

            this.beforeAddToCart(button);

            this.addToCart(button).then(() => {
                this.afterAddToCart();
            });
        }

        /**
         * Handles click on add to cart button.
         * - If single size,  directly adds to cart
         * - If multiple sizes, validates selection, fetches variant data, then adds to cart
         *
         * atcBtn {TAddToCartButtonInstance} - The add to cart button instance.
         */
        handleClick(atcBtn: TAddToCartButtonInstance) {
            if (atcBtn.config.isSingleSize) {
                this.config.readyToOrder = true;
                this.handleAddToCart(atcBtn);

                return;
            }

            const inputSelect = this.getActiveSizeSelect();
            const option = inputSelect?.options[inputSelect.selectedIndex];

            if (!option || inputSelect.selectedIndex === 0) {
                this.callIfExists('showCartMessage', this.prefs().textSelectSize, this);

                return;
            }

            const url: string = option.dataset.attrUrl || '';

            const requestObject = submitFormJsonWithAbort(url, undefined, 'GET', true);

            requestObject.promise
                .then(response => {
                    if (!atcBtn || !atcBtn.config) {
                        return;
                    }

                    atcBtn.refs?.self?.get(0)?.setAttribute('data-pid', response.product?.id);
                    atcBtn.config.pid = response.product?.id;

                    this.setPrefs({ readyToOrder: response.product?.readyToOrder });

                    this.handleAddToCart(atcBtn);
                })
                .catch(() => {
                    this.callIfExists('showCartMessage', this.prefs().textNetworkError, true);
                })
                .finally(() => {
                    this.setPref('processingRequest', false);
                    atcBtn.stopProcess();
                });
        }

        /**
         * Shows size selectors for the selected color and disables selectors for hidden color groups.
         *
         * link {RefElement} - RefElement containing the selected swatch button.
         */
        handleSwatchSelection(link: RefElement) {
            const linkEl = link?.get(0) as HTMLElement;
            const selectedColorId = linkEl?.dataset.attrValue;
            const dropdownSizeSelectors: Array<HTMLElement> = Array.from(
                this.refs?.self?.get(0)?.querySelectorAll<HTMLElement>('.b-crossell_color-group') || []
            );

            if (!dropdownSizeSelectors.length || !selectedColorId) {
                return;
            }

            dropdownSizeSelectors.forEach(selector => {
                const innerSelect = selector.querySelector<HTMLSelectElement>('select');
                const isSelectedColor = selector.dataset.colorId === selectedColorId;

                if (isSelectedColor) {
                    selector.removeAttribute('hidden');
                    innerSelect?.removeAttribute('data-skip-validation');
                    innerSelect?.setAttribute('aria-invalid', 'true');
                    innerSelect?.removeAttribute('disabled');

                    return;
                }

                selector.setAttribute('hidden', 'hidden');
                innerSelect?.setAttribute('data-skip-validation', 'true');
                innerSelect?.removeAttribute('aria-invalid');
                innerSelect?.setAttribute('disabled', 'disabled');

                if (innerSelect) {
                    innerSelect.selectedIndex = 0;
                }
            });
        }

        /**
         * Returns the size select from the currently visible color group, if color-specific groups exist.
         *
         * returns {HTMLSelectElement | null} Active size select element.
         */
        getActiveSizeSelect(): HTMLSelectElement | null {
            const rootEl = this.refs.self.get(0);
            const activeColorGroup = rootEl?.querySelector<HTMLElement>('.b-crossell_color-group:not([hidden])');
            const selectScope = activeColorGroup || rootEl;
            const selectDiv = selectScope?.querySelector('.b-select[aria-hidden="false"]');

            return selectDiv?.querySelector<HTMLSelectElement>('select') || null;
        }

        /**
         * Updates add-to-cart product ID for single-size color selection before submitting.
         *
         * button {TAddToCartButtonInstance} - The add to cart button instance.
         */
        beforeAddToCart(button: TAddToCartButtonInstance) {
            const isColorSelectionEnabled = button.config.isColorSelection;
            const isSingleSize = button.config.isSingleSize;

            if (button?.config?.colorVariantsMapping && isColorSelectionEnabled && isSingleSize) {
                const selectedSwatch = this.ref('swatchesContainer')?.get(0)?.querySelector('.m-selected') as HTMLElement;
                const colorId = selectedSwatch?.dataset?.attrValue || '';

                if (!colorId) {
                    return;
                }

                const variantId = button.config.colorVariantsMapping[colorId];

                if (!variantId) {
                    return;
                }

                button.config.pid = variantId;
            }
        }

        /**
         * Handles after add to cart actions, namely triggering click
         * for OptimizelyWidget
         */
        afterAddToCart() {
            const cartActionsEl = document.querySelector('.b-crossell_product-actions') as HTMLElement;
            const cartActionsRefEl = new RefElement([cartActionsEl]);

            this.eventBus().emit('optimizely:trackClick', cartActionsRefEl);
        }

        /**
         * Handles selection of a size toggle button.
         * Marks the button as selected,
         * displays associated select element and
         * clears selection from other buttons
         *
         * el {RefElement} - RefElement containing the clicked button element.
         */
        toggleBtnSizeSelection(el: RefElement) {
            this.clearMessages();

            const button = el?.get(0) as HTMLButtonElement;

            if (button && button.getAttribute(ariaChecked) === 'false') {
                button.setAttribute(ariaChecked, 'true');
                const sizeSelectEl = this.getControlledElement(button);

                if (sizeSelectEl) {
                    this.toggleHidden(sizeSelectEl);
                }

                this.clearCurrentSelection(button);
            }
        }

        /**
         * Toggles visibility of an element using both `hidden` and `aria-hidden`.
         *
         * el {HTMLElement | HTMLButtonElement} - Element to toggle visibility for.
         */
        toggleHidden(el: HTMLElement | HTMLButtonElement) {
            const isHidden = el.hasAttribute('hidden');
            const newVal = isHidden ? 'false' : 'true';

            el?.setAttribute('aria-hidden', newVal);

            if (isHidden) {
                el?.removeAttribute('hidden');
            } else {
                el?.setAttribute('hidden', 'hidden');
            }
        }

        /**
         * Retrieves the element controlled by a button via its `data-controls` attribute.
         *
         * button {HTMLButtonElement} - The button controlling a target element.
         * returns {HTMLElement | null} The controlled element, if found.
         */
        getControlledElement(button: HTMLButtonElement): HTMLElement | null {
            const controlledElId = button.getAttribute('data-controls');
            const colorGroup = button.closest('.b-crossell_color-group');
            const controlScope = colorGroup || this.ref('variantSelect')?.get(0);
            const controlledEl = controlScope?.querySelector(`#${controlledElId}`) as HTMLElement;

            return controlledEl;
        }

        /**
         * Clears selection from all other size buttons.
         * Sets `aria-checked="false"` on others,
         * hides their associated select elements
         *
         * button {HTMLButtonElement} - The currently selected button.
         */
        clearCurrentSelection(button: HTMLButtonElement) {
            const unselectButtons: NodeListOf<HTMLButtonElement> | undefined = this.ref('self')
                .get(0)
                ?.querySelectorAll(`.b-variation_swatch:not(#${button.id})`);

            unselectButtons?.forEach(btn => {
                btn.setAttribute(ariaChecked, 'false');
                const controlledEl = this.getControlledElement(btn) as HTMLElement;

                if (!controlledEl?.hasAttribute('hidden')) {
                    this.toggleHidden(controlledEl);
                }
            });
        }

        /**
         * Clear error messages, if present, on size selection changes.
         */
        clearMessages() {
            this.callIfExists('hideCartMessage');
        }
    }

    return ProductDetailCrossSellMixin;
}

export type TProductDetailCrossSellMixin = ReturnType<typeof ProductDetailCrossSellMixinClassCreator>;

export type TProductDetailCrossSellMixinInstance = InstanceType<TProductDetailCrossSellMixin>;

export default ProductDetailCrossSellMixinClassCreator;
